import axios from "axios";
import { api } from "@shared/api/client";
import type {
  BibliotecaFilters,
  BibliotecaListResponse,
  BibliotecaMetadataPayload,
  BibliotecaStandard,
  BibliotecaSummaryResponse,
  BibliotecaTipo,
} from "../types/biblioteca";

function endpoint(standard: BibliotecaStandard) {
  return `/certificaciones/${standard}/biblioteca`;
}

export async function getBibliotecaSummary(
  standard: BibliotecaStandard,
  signal?: AbortSignal,
) {
  const { data } = await api.get<BibliotecaSummaryResponse>(
    `${endpoint(standard)}/resumen`,
    { signal },
  );

  return data;
}

export async function getBibliotecaDocuments(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  filters: BibliotecaFilters,
  signal?: AbortSignal,
) {
  const { data } = await api.get<BibliotecaListResponse>(
    `${endpoint(standard)}/${tipo}`,
    {
      params: {
        search: filters.search || undefined,
        estado: filters.estado || undefined,
        carga_desde: filters.carga_desde || undefined,
        carga_hasta: filters.carga_hasta || undefined,
        vigencia_desde: filters.vigencia_desde || undefined,
        vigencia_hasta: filters.vigencia_hasta || undefined,
        page: filters.page,
        per_page: 25,
      },
      signal,
    },
  );

  return data;
}

export async function uploadBibliotecaDocument(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  file: File,
  payload: BibliotecaMetadataPayload,
) {
  const formData = new FormData();
  formData.append("documento", file);
  if (payload.fecha_vigencia) {
    formData.append("fecha_vigencia", payload.fecha_vigencia);
  }
  if (payload.observaciones.trim()) {
    formData.append("observaciones", payload.observaciones.trim());
  }

  await api.post(`${endpoint(standard)}/${tipo}`, formData);
}

export async function updateBibliotecaDocument(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  id: number,
  payload: BibliotecaMetadataPayload,
) {
  await api.put(`${endpoint(standard)}/${tipo}/${id}`, {
    fecha_vigencia: payload.fecha_vigencia || null,
    observaciones: payload.observaciones.trim() || null,
  });
}

export async function deleteBibliotecaDocument(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  id: number,
) {
  await api.delete(`${endpoint(standard)}/${tipo}/${id}`);
}

export async function getBibliotecaPreview(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  id: number,
) {
  const { data } = await api.get<Blob>(
    `${endpoint(standard)}/${tipo}/${id}/preview`,
    { responseType: "blob" },
  );

  return data;
}

export async function downloadBibliotecaDocument(
  standard: BibliotecaStandard,
  tipo: BibliotecaTipo,
  id: number,
  filename: string,
) {
  const { data } = await api.get<Blob>(
    `${endpoint(standard)}/${tipo}/${id}/download`,
    { responseType: "blob" },
  );
  const url = URL.createObjectURL(data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function bibliotecaApiError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (
      responseData &&
      typeof responseData === "object" &&
      "message" in responseData &&
      typeof responseData.message === "string"
    ) {
      return responseData.message;
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      "errors" in responseData &&
      responseData.errors &&
      typeof responseData.errors === "object"
    ) {
      const first = Object.values(responseData.errors).flat().find((value) =>
        typeof value === "string",
      );

      if (typeof first === "string") {
        return first;
      }
    }
  }

  return fallback;
}
